import React, { useState, useRef } from 'react';
import '../css/textField.css';

export interface TextFieldProps {
  id?: string;
  name?: string;
  value?: string;
  label?: string;
  placeholder?: string;
  onChange?: (value: string) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  multiline?: boolean;
  rows?: number;
  maxLength?: number;
  assistiveText?: string;
  error?: boolean;
  errorText?: string;
  warning?: boolean;
  warningText?: string;
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url';
}

const TextField: React.FC<TextFieldProps> = ({
  id,
  name,
  value = '',
  label,
  placeholder,
  onChange,
  onFocus,
  onBlur,
  required = false,
  disabled = false,
  className = '',
  multiline = false,
  rows = 3,
  maxLength,
  assistiveText,
  error = false,
  errorText,
  warning = false,
  warningText,
  type = 'text',
}) => { //NOSONAR
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  const displayLabel = label || placeholder;
  const hasValue = value && value.length > 0;
  const showFloatingLabel = isFocused || hasValue;

  const hasAsterisk = displayLabel?.includes('*');
  const labelText = hasAsterisk
    ? displayLabel?.replace(' *', '').replace('*', '')
    : displayLabel;

  const renderLabel = () => {
    /* istanbul ignore next */
    if (!displayLabel) return null;
    return (
      <>
        {labelText}
        {(hasAsterisk || required) && (
          <span className="scl--cms-components-textField-requiredAsterisk"> *</span>
        )}
      </>
    );
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setIsFocused(true);
    if (onFocus) onFocus(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setIsFocused(false);
    if (onBlur) onBlur(e);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (onChange) onChange(e.target.value);
  };

  const handlePlateClick = () => {
    inputRef.current?.focus();
  };

  // Determine assistive message and state
  const showError = error && errorText;
  const showWarning = !error && warning && warningText;

  const assistiveMessage = showError
    ? errorText
    : showWarning //NOSONAR
      ? warningText
      : assistiveText;

  const assistiveClass = showError
    ? 'scl--cms-components-textField-assistive--error'
    : showWarning //NOSONAR
      ? 'scl--cms-components-textField-assistive--warning'
      : '';

  const plateClasses = [
    'scl--cms-components-textField-plate',
    multiline ? 'scl--cms-components-textField--multiline' : '',
    isFocused ? 'scl--cms-components-textField--focused' : '',
    error ? 'scl--cms-components-textField--error' : '',
    warning && !error ? 'scl--cms-components-textField--warning' : '',
    disabled ? 'scl--cms-components-textField--disabled' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={`scl--cms-components-textField-container ${className}`}>
      <div className={plateClasses} onClick={handlePlateClick} //NOSONAR
      >
        {/* Floating Label */}
        {displayLabel && (
          <label
            className={`scl--cms-components-textField-label ${showFloatingLabel ? 'scl--cms-components-textField-label--floating' : ''
              }`}
          >
            {renderLabel()}
          </label>
        )}

        <div className="scl--cms-components-textField-wrapper">
          {multiline ? (
            <textarea
              ref={inputRef as React.RefObject<HTMLTextAreaElement>}
              id={id}
              name={name}
              value={value}
              rows={rows}
              maxLength={maxLength}
              disabled={disabled}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              className="scl--cms-components-textField-input scl--cms-components-textField-input--textarea"
              autoComplete="off"
            />
          ) : (
            <input
              ref={inputRef as React.RefObject<HTMLInputElement>}
              id={id}
              name={name}
              type={type}
              value={value}
              maxLength={maxLength}
              disabled={disabled}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              className="scl--cms-components-textField-input"
              autoComplete="off"
            />
          )}
        </div>
      </div>

      {/* Assistive / Error / Warning Text */}
      {(assistiveMessage) && (
        <div className={`scl--cms-components-textField-assistive ${assistiveClass}`}>
          {assistiveMessage}
        </div>
      )}
    </div>
  );
};

export default TextField;